import { ChevronLeft, ChevronRight, FilterAlt, LocationOn } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { MatxLoading } from "app/components";
import ReadMoreText from "app/components/ReadMoreText";
import useGetJobs from "app/hooks/job/useGetJobs";
import useToggleBookmark from "app/hooks/candidate/useToggleBookmark";
import useAuth from "app/hooks/useAuth";
import { AppRoutes } from "app/utils/global_constants";
import {
  buildJobDetailPath,
  buildJobSearchUrl,
  getJobSearchSeo,
  isNonProductionIndexingHost,
  parseJobSearchLocation
} from "app/utils/jobSearchSeo";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import bgWaveImg from "../../../assets/illustrations/bg-img.png";
import FilterWrapper from "./shared/FilterWrapper";
import ResponsiveFilters from "./shared/ResponsiveFilters";
import SquareImage from "./shared/SquareImage";
import { bgWave } from "./Constants";
import { formatExp } from "app/utils/utils";
import AppliedText from "./shared/Job Detail/AppliedText";
import BookmarkButton from "./shared/BookmarkButton";

// Parse experience range into [min, max] for backend
const parseExperienceRange = (exp) => {
  // Handle null, undefined, or "all" values
  if (!exp || exp === "all") return null;

  // Convert to string if it's a number
  const expStr = String(exp);

  // Handle special case for 7+ years
  if (expStr === "7+") return [7, 100];

  // Handle range values (e.g., "0-1", "1-3", etc.)
  if (expStr.includes("-")) {
    const [min, max] = expStr.split("-").map(Number);
    if (isNaN(min) || isNaN(max)) {
      console.warn("Invalid experience range:", exp);
      return null;
    }
    return [min, max];
  }

  // Handle single number values
  const numExp = Number(exp);
  if (!isNaN(numExp)) {
    return [numExp, numExp];
  }

  console.warn("Invalid experience value:", exp);
  return null;
};

const collapseHeaderSx = {
  cursor: "pointer",
  bgcolor: "rgba(95, 75, 232, 0.06)",
  border: "1px solid rgba(95, 75, 232, 0.1)",
  borderRadius: 1.5,
  px: 1.5,
  py: 1
};

const isSalaryNotDisclosed = (minSalary, maxSalary) =>
  Number(minSalary) === 0 && Number(maxSalary) === 0;

const formatJobSalary = (minSalary, maxSalary) => {
  if (isSalaryNotDisclosed(minSalary, maxSalary)) {
    return "Not Disclosed";
  }

  return `₹${minSalary || 0} - ₹${maxSalary || 0}`;
};

function Jobs() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const initialUrlState = parseJobSearchLocation(location.pathname, location.search);

  // Initial URL params extraction
  const jobIdFromUrl = initialUrlState.jobId || "";
  const initialSearchTerm = initialUrlState.jobTitle || "";
  const initialLocation = initialUrlState.city || "";
  const initialLocType = initialUrlState.locationType || "";
  const initialType = initialUrlState.type || "";
  const initialCompany = initialUrlState.company || "";

  // Job Detail related state
  const [open, setOpen] = useState({
    overview: true,
    responsibilities: true,
    benefits: true,
    skills: true,
    aboutCompany: false
  });

  // State initialization with URL parameters
  const [selectedJobId, setSelectedJobId] = useState(jobIdFromUrl || null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [searchMode, setSearchMode] = useState(
    initialSearchTerm ? "job" : initialCompany ? "company" : "all"
  );

  const [inputSearch, setInputSearch] = useState(initialSearchTerm);
  const [inputCity, setInputCity] = useState(initialLocation || "");

  const [filter, setFilter] = useState({
    type: initialType ? initialType.split(",").filter(Boolean) : [],
    location: initialLocType ? initialLocType.split(",").filter(Boolean) : [],
    salary: null,
    exp: "all",
    skill: initialSearchTerm,
    city: initialLocation,
    state: initialLocation,
    company: initialCompany
  });
  const [pagination, setPagination] = useState({
    limit: 5,
    offset: 0,
    orderBy: "jp_created_at",
    sortOrder: "DESC",
    searchQuery: initialSearchTerm
  });
  const [openFilters, setOpenFilters] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Prevents initial double fetch
  const { mutate: toggleBookmark, isPending: isBookmarkUpdating } = useToggleBookmark();

  // Replace historical malformed/filter URLs before they can be indexed again.
  useEffect(() => {
    if (initialUrlState.shouldRedirect) {
      window.location.replace(initialUrlState.normalizedUrl);
    }
  }, [initialUrlState.normalizedUrl, initialUrlState.shouldRedirect]);

  const updateSearchQuery = useCallback(
    (value) => {
      const trimmedValue = value.trim();
      // Update both the search term state
      setSearchTerm(trimmedValue);

      // Reset pagination when search changes
      setPagination((prev) => ({ ...prev, offset: 0 }));

      navigate(buildJobSearchUrl({ jobTitle: trimmedValue, city: inputCity }), {
        replace: true
      });
    },
    [inputCity, navigate]
  );

  // Filter handlers
  const handleSearch = useCallback(
    (e) => {
      setSearchTerm(inputSearch);
      setFilter((prev) => {
        return {
          ...prev,
          skill: inputSearch,
          city: inputCity,
          state: inputCity
        };
      });

      navigate(buildJobSearchUrl({ jobTitle: inputSearch, city: inputCity }), {
        replace: true
      });
    },
    [inputCity, inputSearch, navigate]
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    updateSearchQuery("");
  }, [updateSearchQuery]);

  const handleClearAllFilters = useCallback(() => {
    // Clear all filter states
    setFilter({
      type: [],
      location: [],
      salary: [0, 30],
      exp: "all",
      skill: "",
      city: "",
      state: "",
      company: ""
    });

    // Clear search states
    setSearchTerm("");
    setInputSearch("");
    setInputCity("");
    setSearchMode("all");

    // Reset pagination
    setPagination((prev) => ({
      ...prev,
      offset: 0,
      searchQuery: ""
    }));

    navigate("/jobs", { replace: true });
  }, [navigate]);

  // Generic filter change handler to reduce duplication
  const handleFilterChange = useCallback(
    (key, value) => {
      setFilter((prev) => ({ ...prev, [key]: value }));
      setPagination((prev) => ({ ...prev, offset: 0 }));
      if (isMobile) {
        setOpenFilters(false);
      }
    },
    [isMobile]
  );

  // Specialized handler for company autocomplete (value is an object)
  const handleCompanyChange = useCallback(
    (event, value) => {
      let companyValue = value?.id || "";
      handleFilterChange("company", companyValue);
    },
    [handleFilterChange]
  );

  // Shorthand handlers for other filters
  const handleJobTypeChange = useCallback(
    (value) => handleFilterChange("type", value),
    [handleFilterChange]
  );
  const handleLocationChange = useCallback(
    (value) => handleFilterChange("location", value),
    [handleFilterChange]
  );
  const handleSalaryChange = useCallback(
    (range) => handleFilterChange("salary", range),
    [handleFilterChange]
  );
  const handleExpChange = useCallback(
    (expLevel) => handleFilterChange("exp", expLevel),
    [handleFilterChange]
  );
  const handleCityChange = useCallback(
    (value) => handleFilterChange("city", value),
    [handleFilterChange]
  );

  const handleViewJobDetails = useCallback(
    (jobId) => {
      setSelectedJobId(jobId);
      if (isMobile) {
        setTimeout(() => {
          document.getElementById("job-details-section")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    },
    [isMobile]
  );

  const navigateToCompanyDetailPage = useCallback(
    (selectedJob) => {
      navigate(`/company/${selectedJob?.company?.jp_id}`);
    },
    [navigate]
  );

  const handleOpenApplyDialog = useCallback(
    (id, jobTitle) => {
      if (!user) {
        navigate(AppRoutes.ROUTE_LOGIN_CANDIDATE, {
          state: { selectedJobId: id, returnTo: location.pathname + location.search }
        });
      } else {
        navigate(buildJobDetailPath(jobTitle, id));
      }
    },
    [user, navigate, location.pathname, location.search]
  );

  const handleToggleBookmark = useCallback(
    (jobId) => {
      if (!user) {
        navigate(AppRoutes.ROUTE_LOGIN_CANDIDATE, {
          state: { selectedJobId: jobId, returnTo: location.pathname + location.search }
        });
        return;
      }

      toggleBookmark({ jobId });
    },
    [user, navigate, location.pathname, location.search, toggleBookmark]
  );

  const handleChangePage = useCallback(
    ({ selected }) => {
      setPagination((prev) => ({ ...prev, offset: selected * prev.limit }));
    },
    []
  );

  // Fetch jobs with all filters
  const {
    data: jobs,
    isFetched: isJobsFetched,
    isLoading: isFetchingJobs
  } = useGetJobs({
    sortOrder: pagination.sortOrder,
    column: pagination.orderBy,
    enabled: true,
    refetchOnMount: false,
    limit: pagination.limit,
    offset: pagination.offset,
    searchTerm: searchTerm?.trim(),
    searchMode,
    type: filter.type,
    exp: parseExperienceRange(filter.exp),
    skill: filter.skill,
    location: filter.location,
    salary: filter.salary ? [filter.salary[0] * 100000, filter.salary[1] * 100000] : null,
    city: filter.city,
    state: filter.state,
    company: filter.company,
    fetchCompanyLogo: false,
    staleTime: 60_000
  });

  useEffect(() => {
    if (!isJobsFetched) return undefined;

    const seo = getJobSearchSeo(location.pathname, location.search);
    if (!seo) return undefined;

    const robotsTag = document.head.querySelector('meta[name="robots"]');
    if (isNonProductionIndexingHost(window.location.hostname)) {
      robotsTag?.setAttribute("content", "noindex, nofollow");
      document.getElementById("collection-schema")?.remove();
      document.getElementById("job-collection-schema")?.remove();
      return undefined;
    }

    if (!jobs?.data?.length) {
      robotsTag?.setAttribute("content", "noindex, follow");
      return undefined;
    }

    robotsTag?.setAttribute("content", seo.robots);
    document.getElementById("collection-schema")?.remove();
    document.getElementById("job-collection-schema")?.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${seo.canonicalUrl}#collectionpage`,
      url: seo.canonicalUrl,
      name: seo.heading,
      description: seo.description,
      mainEntity: {
        "@type": "ItemList",
        name: seo.heading,
        numberOfItems: jobs.count || jobs.data.length,
        itemListElement: jobs.data.map((job, index) => ({
          "@type": "ListItem",
          position: pagination.offset + index + 1,
          url: `${window.location.origin}${buildJobDetailPath(job.jp_title, job.jp_id)}`,
          name: job.jp_title
        }))
      }
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "job-collection-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => script.remove();
  }, [isJobsFetched, jobs, location.pathname, location.search, pagination.offset]);

  let selectedJob = jobs?.data?.length > 0 ? jobs.data[0] : null;
  if (selectedJobId && jobs?.data?.length > 0) {
    selectedJob = jobs?.data?.find((job) => job.jp_id === selectedJobId) || null;
  }
  let jobSkills = selectedJob
    ? selectedJob.jp_required_skills?.split("|").map((skill) => skill.trim()) || []
    : [];
  const jobType = selectedJob?.jp_type || "Not disclosed";
  const city = selectedJob?.jp_city
    ? selectedJob?.jp_city?.split("| ").join(", ")
    : "Not disclosed";
  const workMode = selectedJob?.jp_location || "Not disclosed";

  const experience = selectedJob
    ? `${formatExp(selectedJob.jp_min_exp)}-${formatExp(selectedJob.jp_max_exp)} yrs`
    : "Not disclosed";
  const salary = selectedJob
    ? formatJobSalary(selectedJob.jp_minimum_salary, selectedJob.jp_maximum_salary)
    : "Not disclosed";
  const benefits = selectedJob?.Benefits
    ? selectedJob.Benefits.map((b) => b.jp_benefit.trim())
    : [];

  const pageCount = jobs ? Math.ceil(jobs.count / pagination.limit) : 0;
  const jobPageSeo = getJobSearchSeo(location.pathname, location.search);

  return (
    <>
      <Box sx={bgWave}>
        {/* Top Search + Filters */}
        <Box
          component={"div"}
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            gap: 2,
            zIndex: 1
          }}
        >
          <Box
            sx={{
              zIndex: 0,
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              overflow: "hidden"
            }}
          >
            <img src={bgWaveImg} alt="Background" style={{ width: "100%", height: "400px" }} />
            {/* Gradient overlay for fade-out effect at the bottom */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                opacity: 0.4,
                background: "linear-gradient(180deg, #fff 0%, #faf8ff 100%)"
              }}
            />
          </Box>
          <Box
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
              maxWidth: 800,
              width: "100%",
              zIndex: 1,
              mx: "auto"
            }}
          >
            {/* Title Section */}
            <Box
              component={"header"}
              sx={{
                textAlign: "left",
                display: "block",
                zIndex: 1
              }}
            >
              <Typography component="h1" variant="h4" fontWeight="bold">
                {jobPageSeo?.heading || "Find Your Dream Job"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {jobPageSeo?.description ||
                  "Browse thousands of job opportunities from top companies"}
              </Typography>
            </Box>
            {/* Search Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                borderRadius: 3,
                zIndex: 1
              }}
            >
              <Paper
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 2,
                  boxShadow: 1,
                  pr: 1,
                  minWidth: 320
                }}
              >
                <SearchIcon sx={{ color: "#888", ml: 1 }} />
                <InputBase
                  placeholder="Job title, skills, company"
                  value={inputSearch}
                  onChange={(e) => setInputSearch(e.target.value)}
                  sx={{
                    ml: 1,
                    flex: 1,
                    fontSize: 17,
                    p: "8px 0"
                  }}
                />
              </Paper>
              <Paper
                sx={{
                  display: {
                    xs: "none",
                    sm: "flex"
                  },
                  alignItems: "center",
                  borderRadius: 2,
                  boxShadow: 1,
                  pr: 1,
                  minWidth: 180
                }}
              >
                <LocationOnOutlinedIcon sx={{ color: "#888", ml: 1 }} />
                <InputBase
                  placeholder="City or State"
                  value={inputCity}
                  onChange={(e) => setInputCity(e.target.value)}
                  sx={{
                    ml: 1,
                    flex: 1,
                    fontSize: 17,
                    p: "8px 0"
                  }}
                />
              </Paper>
              <Button
                variant="contained"
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  background: "linear-gradient(90deg, #5f4be8 0%, #6a7cf7 100%)",
                  color: "#fff",
                  width: { xs: "100%", sm: "auto" },
                  fontWeight: 600,
                  fontSize: 16,
                  px: 3,
                  py: 1.2,
                  boxShadow: "0 2px 8px 0 #e0e7ff",
                  transition: "background 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #4b3bbd 0%, #6a7cf7 100%)"
                  }
                }}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Mobile Filters */}
        <Box
          sx={{
            width: "100%",
            display: { xs: "flex", sm: "none" },
            justifyContent: "flex-start",
            px: 2
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setOpenFilters(true)}
            startIcon={<FilterAlt sx={{ color: "gray" }} />}
            sx={{
              fontSize: 14,
              alignItems: "center"
            }}
          >
            Filters
          </Button>
        </Box>
        {/* Main Layout */}
        <Grid container spacing={2} p={2}>
          {/* Sidebar Filters (Desktop Only) */}
          <Grid item md={3} display={{ xs: "none", sm: "block" }}>
            <Paper
              elevation={4}
              sx={{
                borderRadius: 3,
                position: "sticky",
                height: "fit-content",
                zIndex: 1
              }}
            >
              <ResponsiveFilters
                filter={filter}
                searchTerm={searchTerm}
                recentSearches={<></>}
                handleCompanyChange={handleCompanyChange}
                handleJobTypeChange={handleJobTypeChange}
                handleSalaryChange={handleSalaryChange}
                handleLocationChange={handleLocationChange}
                handleExpChange={handleExpChange}
                handleCityChange={handleCityChange}
                handleSearch={handleSearch}
                handleClearSearch={handleClearSearch}
                handleClearAllFilters={handleClearAllFilters}
              />
            </Paper>
          </Grid>

          {/* Job Listings */}
          <Grid item xs={12} md={6} sx={{ height: "100%" }}>
            {isFetchingJobs && <MatxLoading />}
            {jobs?.data?.length > 0 && (
              <>
                <Box display={"flex"} justifyContent={"space-between"} pt={1} zIndex={1}>
                  <Typography variant="h6" fontWeight="bold" mb={1} zIndex={1}>
                    Job Listings {jobs?.count ? `(${jobs.count})` : ""}
                  </Typography>
                  {/* Pagination Prev and Next */}
                  <Box zIndex={1}>
                    <IconButton
                      disabled={pagination.offset === 0}
                      onClick={() => {
                        const prevPage = Math.max(0, pagination.offset / pagination.limit - 1);
                        handleChangePage({ selected: prevPage });
                      }}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <Typography variant="body2" component="span" sx={{ mx: 1 }}>
                      Page {Math.floor(pagination.offset / pagination.limit) + 1} of {pageCount}
                    </Typography>
                    <IconButton
                      disabled={
                        pagination.offset + pagination.limit >= (jobs?.count || 0) || pageCount <= 1
                      }
                      onClick={() => {
                        const nextPage = Math.min(
                          pageCount - 1,
                          pagination.offset / pagination.limit + 1
                        );
                        handleChangePage({ selected: nextPage });
                      }}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 2 }}>
                  {jobs?.data?.map((job) => (
                    <Paper
                      key={job.jp_id}
                      onClick={() => handleViewJobDetails(job.jp_id)}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        cursor: "pointer",
                        border:
                          selectedJobId === job.jp_id ? "2px solid #6366f1" : "1px solid #e0e0e0",
                        transition: "0.3s",
                        "&:hover": {
                          boxShadow: 3,
                          transform: "translateY(-2px)"
                        },
                        zIndex: 1
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "start",
                          gap: 2,
                          mb: { xs: 1, sm: 0 },
                          zIndex: 1
                        }}
                      >
                        <SquareImage
                          companyLogo={job?.company?.jp_logo}
                          companyId={job?.company?.jp_id}
                          companyName={job?.company?.jp_company_name}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            component={"a"}
                            href={buildJobDetailPath(job.jp_title, job.jp_id)}
                            fontWeight="bold"
                            fontSize={"1.2rem"}
                            sx={{ "&:hover": { textDecoration: "underline" } }}
                          >
                            {job.jp_title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {job?.company?.jp_company_name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap", zIndex: 1 }}>
                            {/* Job Type */}
                            <Chip label={job?.jp_type} size="small" />
                            {/* Location Type */}
                            {job?.jp_location &&
                              job?.jp_location
                                .split("| ")
                                .map((loc, index) => <Chip key={index} label={loc} size="small" />)}
                            {/* Experience */}
                            <Chip label={experience} size="small" />
                          </Box>
                          {/* Only display on xs */}
                          <Typography
                            fontWeight="bold"
                            color="primary"
                            display={{ xs: "block", sm: "none" }}
                            mt={1}
                          >
                            {formatJobSalary(job?.jp_minimum_salary, job?.jp_maximum_salary)}
                          </Typography>
                        </Box>
                        {/* Only display on sm and above */}
                        <Typography
                          fontWeight="bold"
                          color="primary"
                          display={{ xs: "none", sm: "block" }}
                        >
                          {formatJobSalary(job?.jp_minimum_salary, job?.jp_maximum_salary)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 1,
                            ml: "59px",
                            zIndex: 1
                          }}
                        >
                          <LocationOn sx={{ color: "gray" }} />
                          <Typography variant="body1">
                            {job.jp_city?.split("| ").join(", ") || "No specific city"}
                          </Typography>
                        </Box>
                        {job?.isApplied ? (
                          <AppliedText />
                        ) : (
                          <Box sx={{ display: "flex" }}>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                borderRadius: "10px",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #5f4be8 0%, #6a7cf7 100%)",
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: 16,
                                px: 3,
                                py: 1,
                                boxShadow: "0 2px 8px 0 #e0e7ff",
                                transition: "background 0.2s",
                                "&:hover": {
                                  background: "linear-gradient(90deg, #4b3bbd 0%, #6a7cf7 100%)"
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenApplyDialog(job.jp_id);
                              }}
                            >
                              Apply Now
                            </Button>
                            <BookmarkButton
                              isBookmarked={job?.isBookmarked}
                              disabled={isBookmarkUpdating}
                              stopPropagation
                              jobTitle={job?.jp_title}
                              onClick={() => handleToggleBookmark(job.jp_id)}
                              sx={{
                                ml: 1,
                                width: 40,
                                height: 40,
                                color: job?.isBookmarked ? "primary.main" : "text.secondary"
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </>
            )}
            {/* No Jobs Found */}
            {!isFetchingJobs && jobs?.data?.length === 0 && (
              <Container
                maxWidth="md"
                sx={{
                  minHeight: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Paper
                  sx={{
                    p: 4,
                    mx: "auto",
                    borderRadius: 3,
                    minHeight: 320,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 16px 0 #e0e7ff",
                    bgcolor: "#f7f9fd",
                    zIndex: 2
                  }}
                >
                  <SearchOffIcon sx={{ fontSize: 60, color: "#bdbdbd", mb: 2 }} />
                  <Typography variant="h5" fontWeight={700} color="#5f4be8" mb={1}>
                    No Jobs Found
                  </Typography>
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    We couldn't find any jobs matching your criteria.
                    <br />
                    Try adjusting your filters or search terms.
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: "16px", mt: 2 }}
                    onClick={handleClearAllFilters}
                  >
                    Reset Filters
                  </Button>
                </Paper>
              </Container>
            )}
          </Grid>

          {/* Job Details Panel */}
          <Grid item xs={12} md={3} display={{ xs: "none", sm: "block" }}>
            <Paper
              elevation={4}
              sx={{
                borderRadius: 3,
                position: "sticky",
                height: "100%",
                pt: 1
              }}
            >
              {selectedJob ? (
                <Box
                  // elevation={3}
                  sx={{
                    borderRadius: 3,
                    px: 2,
                    mx: "auto"
                  }}
                >
                  {/* Job Title & Company */}
                  <Typography mt={1} fontWeight={700} fontSize="1.1rem">
                    {selectedJob?.jp_title}
                  </Typography>

                  {/* Header */}
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Company Logo */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden"
                        }}
                      >
                        {/* Replace with <img src={logoUrl} ... /> */}
                        <SquareImage
                          companyLogo={selectedJob?.company?.jp_logo}
                          companyId={selectedJob?.company?.jp_id}
                          companyName={selectedJob?.company?.jp_company_name}
                        />
                      </Box>
                      <Typography color="text.secondary" fontSize={14}>
                        {selectedJob?.company?.jp_company_name || "Unknown Company"}
                      </Typography>
                      <IconButton onClick={() => navigateToCompanyDetailPage(selectedJob)}>
                        <ChevronRight sx={{ color: "#bdbdbd", fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Job Title & Company */}
                  <Typography mt={1} fontWeight={700} fontSize="1.1rem">
                    {selectedJob?.jp_title}
                  </Typography>

                  {/* Chips */}
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    <Chip label={selectedJob?.jp_type} size="small" sx={{ bgcolor: "#f3f4fa" }} />
                    {selectedJob?.jp_location &&
                      selectedJob?.jp_location
                        .split("| ")
                        .map((loc, index) => <Chip key={index} label={loc} size="small" />)}
                    <Chip label={experience} size="small" sx={{ bgcolor: "#f3f4fa" }} />
                    <Chip
                      label={formatJobSalary(
                        selectedJob?.jp_minimum_salary,
                        selectedJob?.jp_maximum_salary
                      )}
                      size="small"
                      sx={{ bgcolor: "#f3f4fa" }}
                    />
                  </Box>

                  {selectedJob?.isApplied ? (
                    <Box
                      sx={{
                        mt: 2,
                        mb: 1,
                        width: "100%",
                        borderRadius: 2,
                        backgroundColor: "#e8f5e9",
                        color: "#2e7d32",
                        fontWeight: 600,
                        fontSize: 16,
                        py: 1.2,
                        textAlign: "center"
                      }}
                    >
                      Applied
                    </Box>
                  ) : (
                    <Button
                      fullWidth
                      onClick={() =>
                        handleOpenApplyDialog(selectedJob?.jp_id, selectedJob?.jp_title)
                      }
                      sx={{
                        my: 1,
                        borderRadius: 2,
                        background: "linear-gradient(90deg, #5f4be8 0%, #6a7cf7 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 16,
                        textTransform: "none",
                        boxShadow: "0 2px 8px 0 #e0e7ff",
                        "&:hover": {
                          background: "linear-gradient(90deg, #4b3bbd 0%, #6a7cf7 100%)"
                        }
                      }}
                    >
                      Apply Now
                    </Button>
                  )}

                  {/* Job Overview */}
                  <Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ cursor: "pointer" }}
                      onClick={() => setOpen((prev) => ({ ...prev, overview: !prev.overview }))}
                    >
                      <Typography fontWeight={600} fontSize={15}>
                        Job Overview
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          transform: open.overview ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.2s"
                        }}
                      />
                    </Box>
                    <Collapse in={open.overview}>
                      <Box mt={1} mb={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <WorkOutlineIcon fontSize="small" color="action" />
                          <Typography fontSize={14}>{jobType}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <LocationOnOutlinedIcon fontSize="small" color="action" />
                          <Typography fontSize={14}>{city}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <BusinessCenterOutlinedIcon fontSize="small" color="action" />
                          <Typography fontSize={14}>{workMode}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography fontSize={14}>{experience}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <MonetizationOnOutlinedIcon fontSize="small" color="action" />
                          <Typography fontSize={14}>{salary}</Typography>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Responsibilities */}
                  <Box mb={2}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={collapseHeaderSx}
                      onClick={() =>
                        setOpen((prev) => ({
                          ...prev,
                          responsibilities: !prev.responsibilities
                        }))
                      }
                    >
                      <Typography fontWeight={600} fontSize={15}>
                        Responsibilities
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          transform: open.responsibilities ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.2s"
                        }}
                      />
                    </Box>
                    <Collapse in={open.responsibilities}>
                      <Typography
                        fontSize={14}
                        color="text.secondary"
                        mt={1}
                        mb={1}
                        component="div"
                        sx={{
                          color: "black",
                          maxHeight: 260,
                          overflowY: "auto",
                          pr: 1
                        }}
                        dangerouslySetInnerHTML={
                          selectedJob?.jp_description
                            ? { __html: selectedJob.jp_description }
                            : { __html: "<span>No description available for this job.</span>" }
                        }
                      />
                    </Collapse>
                  </Box>

                  {/* Key Skills */}
                  <Box mb={2}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={collapseHeaderSx}
                      onClick={() =>
                        setOpen((prev) => ({
                          ...prev,
                          skills: !prev.skills
                        }))
                      }
                    >
                      <Typography fontWeight={600} fontSize={15}>
                        Key Skills
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          transform: open.skills ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.2s"
                        }}
                      />
                    </Box>
                    <Collapse in={open.skills}>
                      <Typography fontSize={14} mt={1} mb={1}>
                        {jobSkills.length > 0
                          ? jobSkills.map((skill, index) => {
                              return (
                                <span key={index}>
                                  • {skill}
                                  <br />
                                </span>
                              );
                            })
                          : "No skills available for this job."}
                      </Typography>
                    </Collapse>
                  </Box>

                  {/* Benefits */}
                  <Box mb={2}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={collapseHeaderSx}
                      onClick={() =>
                        setOpen((prev) => ({
                          ...prev,
                          benefits: !prev.benefits
                        }))
                      }
                    >
                      <Typography fontWeight={600} fontSize={15}>
                        Benefits
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          transform: open.benefits ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.2s"
                        }}
                      />
                    </Box>
                    <Collapse in={open.benefits}>
                      <Typography fontSize={14} mt={1} mb={1}>
                        {benefits.length > 0
                          ? benefits.map((benefit, index) => (
                              <span key={index}>
                                • {benefit}
                                <br />
                              </span>
                            ))
                          : "No benefits available for this job."}
                      </Typography>
                    </Collapse>
                  </Box>

                  {/* About Company */}
                  <Box mb={2}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={collapseHeaderSx}
                      onClick={() =>
                        setOpen((prev) => ({
                          ...prev,
                          aboutCompany: !prev.aboutCompany
                        }))
                      }
                    >
                      <Typography fontWeight={600} fontSize={15}>
                        About Company
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          transform: open.aboutCompany ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.2s"
                        }}
                      />
                    </Box>
                    <Collapse in={open.aboutCompany}>
                      <Typography fontSize={14} mt={1} mb={1}>
                        {selectedJob?.company?.jp_about_company ? (
                          <ReadMoreText
                            text={selectedJob?.company?.jp_about_company}
                            maxChars={200}
                          />
                        ) : (
                          "No information available about the company."
                        )}
                      </Typography>
                    </Collapse>
                  </Box>
                </Box>
              ) : (
                <Box textAlign="center" mt={5} height={"100%"}>
                  <SearchIcon sx={{ fontSize: 50, color: "gray" }} />
                  <Typography>Select a job to view details</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Drawer anchor="right" open={openFilters} onClose={() => setOpenFilters(false)}>
          <FilterWrapper
            empType={filter.type}
            locType={filter.location}
            salary={filter.salary}
            exp={filter.exp}
            city={filter.city}
            searchTerm={searchTerm}
            company={filter.company}
            handleCompanyChange={handleCompanyChange}
            handleJobTypeChange={handleJobTypeChange}
            handleSalaryChange={handleSalaryChange}
            handleLocationChange={handleLocationChange}
            handleExpChange={handleExpChange}
            handleCityChange={handleCityChange}
            handleSearch={handleSearch}
            handleClearSearch={handleClearSearch}
            handleClearAllFilters={handleClearAllFilters}
          />
        </Drawer>
      </Box>
    </>
  );
}

export default Jobs;
